import React, {PropsWithChildren, useContext, useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {UpdateXpertKnowledgeSubclipBody, XpertKnowledgeSectionDto, XpertKnowledgeSubSectionBlobDto} from '../../services/XpertKnowledgeSection/type'
import {useXpertKnowledgeContext} from '../XpertKnowledge/XpertKnowledgeContext'
import {XpertKnowledgeSectionService} from '../../services/XpertKnowledgeSection'
import {
  RequestChangesXpertKnowledgeTutorialBody,
  SubmitXpertKnowledgeTutorialBody,
  UpdateXpertKnowledgeTutorialBody,
  XpertKnowledgeTutorialDto,
  XpertKnowledgeTutorialStatus,
} from '../../services/XpertKnowledgeTutorial/type'
import {XpertKnowledgeDto} from '../../services/XpertKnowledge/type'
import {getCookie, getCookieName} from '../../common/utils/cookieUtils'
import {XpertKnowledgeTutorialService} from '../../services/XpertKnowledgeTutorial/XpertKnowledgeTutorial'

interface XpertKnowledgeTutorialContext {
  xpertKnowledgeSections: XpertKnowledgeSectionDto[]
  xpertKnowledgeTutorials: XpertKnowledgeTutorialDto[]
  selectedXpertKnowledgeTutorial?: XpertKnowledgeTutorialDto
  submitRating: (xpertKnowledgeId: string, tutorialId: number, rating: number) => Promise<void>
  downgradeXpertKnowledgeTutorial: () => Promise<void>
  setSelectedXpertKnowledgeTutorial: (xkTutorial: XpertKnowledgeTutorialDto) => void
  updateXpertKnowledgeTutorial: (body: UpdateXpertKnowledgeTutorialBody) => Promise<void>
  submitXpertKnowledgeTutorial: (body: SubmitXpertKnowledgeTutorialBody) => Promise<void>
  requestChangeXpertKnowledgeTutorial: (body: RequestChangesXpertKnowledgeTutorialBody) => Promise<void>
  dismissRequestChangeXpertKnowledgeTutorial: () => Promise<void>
  dismissReviewXpertKnowledgeTutorial: () => Promise<void>
  approveXpertKnowledgeTutorial: () => Promise<void>
  deleteXpertKnowledgeTutorialSection: (sectionId: number) => void
  deleteXpertKnowledgeTutorialSubSection: (sectionId: number, subSectionId: number) => void
  createXpertKnowledgeTutorialSection: (
    title: string,
    position: number,
    intro?: string,
    conclusion?: string
  ) => Promise<XpertKnowledgeSectionDto | undefined>
  createXpertKnowledgeTutorialSubSection: (sectionId: number, title: string, position: number, content?: string) => void
  updateXpertKnowledgeTutorialSection: (sectionId: number, title: string, intro?: string, conclusion?: string) => void
  updateXpertKnowledgeTutorialSubSection: (
    sectionId: number,
    subSectionId: number,
    title: string,
    medias: XpertKnowledgeSubSectionBlobDto[],
    content?: string,
    position?: number
  ) => void
  updateXpertKnowledgeSubclip: (sectionId: number, subSectionId: number, body: UpdateXpertKnowledgeSubclipBody) => Promise<void>
}

const Context = React.createContext<XpertKnowledgeTutorialContext>(null as any)

interface XpertKnowledgeTutorialContextProviderProps {}

export const XpertKnowledgeTutorialContextProvider: React.FC<PropsWithChildren<XpertKnowledgeTutorialContextProviderProps>> = ({children}) => {
  const {xpertKnowledgeId} = useParams<{xpertKnowledgeId: string}>()
  const {selectedXpertKnowledge, setSelectedXpertKnowledge, fetchXpertKnowledgeById, setXpertKnowledgesUnpublish, xpertKnowledgesUnpublish} =
    useXpertKnowledgeContext()
  const [xpertKnowledgeTutorials, setXpertKnowledgeTutorials] = useState<XpertKnowledgeTutorialDto[]>([])
  const [selectedXpertKnowledgeTutorial, setSelectedXpertKnowledgeTutorial] = useState<XpertKnowledgeTutorialDto | undefined>(undefined)
  const [xpertKnowledgeSections, setXpertKnowledgeSections] = useState<XpertKnowledgeSectionDto[]>([])
  const navigate = useNavigate()

  useEffect(() => {
    if (xpertKnowledgeId) {
      fetchXpertKnowledgeById(xpertKnowledgeId)
        .then((xk) => {
          setSelectedXpertKnowledge(xk)
          fetchAllXpertKnowledgeTutorial(xk)
        })
        .catch((error) => {
          if (!getCookie(getCookieName())) {
            navigate('/login')
          } else {
            console.error('Error fetching xpert knowledge:', error)
            navigate('/xpert-knowledge/error')
          }
        })
    }
  }, [xpertKnowledgeId])

  const fetchAllXpertKnowledgeTutorial = async (xk: XpertKnowledgeDto) => {
    try {
      var tutorials = await XpertKnowledgeTutorialService.getAll(xk.id)
      tutorials = tutorials.filter((xk) => xk.status !== XpertKnowledgeTutorialStatus.DEPRECATED)

      setXpertKnowledgeTutorials(tutorials)
      fetchXpertKnowledgeTutorial(xk.id, tutorials.filter((tut) => tut.status === XpertKnowledgeTutorialStatus.PUBLISHED)[0]?.id ?? tutorials[0]?.id)
    } catch (error) {
      console.error('Error fetching Xpert Knowledge Tutorials:', error)
    }
  }

  const fetchXpertKnowledgeTutorial = async (xpertKnowledgeId: string, xpertKnowledgeTutorialId: number) => {
    try {
      const tutorial = await XpertKnowledgeTutorialService.getById(xpertKnowledgeId, xpertKnowledgeTutorialId)
      setSelectedXpertKnowledgeTutorial(tutorial)
      tutorial.sections.map((section) => {
        section.subSections = section.subSections?.sort((a, b) => a.position - b.position)
        return section
      })
      setXpertKnowledgeSections(tutorial.sections.sort((a, b) => a.position - b.position))
    } catch (error) {
      console.error('Error fetching Xpert Knowledge Tutorial:', error)
    }
  }

  const submitRating = async (xpertKnowledgeId: string, tutorialId: number, rating: number) => {
    const xkTutorial = await XpertKnowledgeTutorialService.submitRating({xpertKnowledgeId, tutorialId, rating})
    setSelectedXpertKnowledgeTutorial(xkTutorial)
  }

  const updateXpertKnowledgeTutorial = async (body: UpdateXpertKnowledgeTutorialBody) => {
    if (!selectedXpertKnowledgeTutorial) return
    const updatedTutorial = await XpertKnowledgeTutorialService.update(selectedXpertKnowledge!.id, selectedXpertKnowledgeTutorial.id, body)
    setXpertKnowledgeSections(updatedTutorial.sections)

    selectedXpertKnowledge!.publishTutorial = updatedTutorial
    setSelectedXpertKnowledge(selectedXpertKnowledge)
    setSelectedXpertKnowledgeTutorial(updatedTutorial)
  }

  const updateXpertKnowledgeSubclip = async (sectionId: number, subSectionId: number, body: UpdateXpertKnowledgeSubclipBody) => {
    if (!selectedXpertKnowledgeTutorial) return
    const updatedSubSection = await XpertKnowledgeSectionService.updateSubclip(
      selectedXpertKnowledge!.id,
      selectedXpertKnowledgeTutorial.id,
      sectionId,
      subSectionId,
      body
    )
    setXpertKnowledgeSections((prev) =>
      prev.map((section) => ({
        ...section,
        subSections: section.subSections?.map((subSection) => (subSection.id === updatedSubSection.id ? updatedSubSection : subSection)),
      }))
    )
  }

  const submitXpertKnowledgeTutorial = async (body: SubmitXpertKnowledgeTutorialBody) => {
    if (!selectedXpertKnowledgeTutorial) return
    try {
      const updatedTutorial = await XpertKnowledgeTutorialService.submit(selectedXpertKnowledge!.id, selectedXpertKnowledgeTutorial.id, body)
      setXpertKnowledgeSections(updatedTutorial.sections)

      selectedXpertKnowledge!.publishTutorial = updatedTutorial
      setSelectedXpertKnowledge(selectedXpertKnowledge)
      setSelectedXpertKnowledgeTutorial(updatedTutorial)
    } catch {
      window.location.reload()
    }
  }

  const requestChangeXpertKnowledgeTutorial = async (body: RequestChangesXpertKnowledgeTutorialBody) => {
    if (!selectedXpertKnowledgeTutorial) return
    try {
      const updatedTutorial = await XpertKnowledgeTutorialService.requestChanges(selectedXpertKnowledge!.id, selectedXpertKnowledgeTutorial.id, body)
      setXpertKnowledgeSections(updatedTutorial.sections)

      selectedXpertKnowledge!.publishTutorial = updatedTutorial
      setSelectedXpertKnowledge(selectedXpertKnowledge)
      setSelectedXpertKnowledgeTutorial(updatedTutorial)
      setXpertKnowledgesUnpublish(
        xpertKnowledgesUnpublish.map((xk) => {
          if (xk.id === selectedXpertKnowledge!.id) {
            return {...xk, publishTutorial: updatedTutorial}
          }
          return xk
        })
      )
    } catch {
      window.location.reload()
    }
  }

  const dismissRequestChangeXpertKnowledgeTutorial = async () => {
    if (!selectedXpertKnowledgeTutorial) return
    try {
      const updatedTutorial = await XpertKnowledgeTutorialService.dismissRequestChanges(selectedXpertKnowledge!.id, selectedXpertKnowledgeTutorial.id)
      setXpertKnowledgeSections(updatedTutorial.sections)

      selectedXpertKnowledge!.publishTutorial = updatedTutorial
      setSelectedXpertKnowledge(selectedXpertKnowledge)
      setSelectedXpertKnowledgeTutorial(updatedTutorial)

      setXpertKnowledgesUnpublish(
        xpertKnowledgesUnpublish.map((xk) => {
          if (xk.id === selectedXpertKnowledge!.id) {
            return {...xk, publishTutorial: updatedTutorial}
          }
          return xk
        })
      )
    } catch {
      window.location.reload()
    }
  }

  const dismissReviewXpertKnowledgeTutorial = async () => {
    if (!selectedXpertKnowledgeTutorial) return
    try {
      const updatedTutorial = await XpertKnowledgeTutorialService.dismissReview(selectedXpertKnowledge!.id, selectedXpertKnowledgeTutorial.id)
      setXpertKnowledgeSections(updatedTutorial.sections)

      selectedXpertKnowledge!.publishTutorial = updatedTutorial
      setSelectedXpertKnowledge(selectedXpertKnowledge)
      setSelectedXpertKnowledgeTutorial(updatedTutorial)

      setXpertKnowledgesUnpublish(
        xpertKnowledgesUnpublish.map((xk) => {
          if (xk.id === selectedXpertKnowledge!.id) {
            return {...xk, publishTutorial: updatedTutorial}
          }
          return xk
        })
      )
    } catch {
      window.location.reload()
    }
  }

  const downgradeXpertKnowledgeTutorial = async () => {
    if (!selectedXpertKnowledgeTutorial) return
    try {
      const updatedTutorial = await XpertKnowledgeTutorialService.downgrade(selectedXpertKnowledge!.id, selectedXpertKnowledgeTutorial.id)
      setXpertKnowledgeSections(updatedTutorial.sections)

      if (selectedXpertKnowledge?.id) {
        setSelectedXpertKnowledge({...selectedXpertKnowledge, publishTutorial: updatedTutorial, isPublic: false})
      } else {
        console.error('Error: selectedXpertKnowledge.id is undefined')
      }
      setSelectedXpertKnowledgeTutorial(updatedTutorial)
    } catch {
      window.location.reload()
    }
  }

  const approveXpertKnowledgeTutorial = async () => {
    try {
      if (!selectedXpertKnowledgeTutorial) return
      const updatedTutorial = await XpertKnowledgeTutorialService.approve(selectedXpertKnowledge!.id, selectedXpertKnowledgeTutorial.id)
      setXpertKnowledgeSections(updatedTutorial.sections)
      setXpertKnowledgeTutorials((prev) => prev.map((tutorial) => (tutorial.id === updatedTutorial.id ? updatedTutorial : tutorial)))
      setSelectedXpertKnowledgeTutorial(updatedTutorial)
      setSelectedXpertKnowledge({...selectedXpertKnowledge!, publishTutorial: selectedXpertKnowledgeTutorial})
    } catch {
      window.location.reload()
    }
  }

  const deleteXpertKnowledgeTutorialSection = async (sectionId: number) => {
    try {
      await XpertKnowledgeSectionService.deleteSection(selectedXpertKnowledge!.id, selectedXpertKnowledgeTutorial!.id, sectionId)
      setXpertKnowledgeSections((prev) => prev.filter((section) => section.id !== sectionId))
    } catch (error) {
      console.error('Error deleting Xpert Knowledge Tutorial Section:', error)
    }
  }

  const deleteXpertKnowledgeTutorialSubSection = async (sectionId: number, subSectionId: number) => {
    try {
      await XpertKnowledgeSectionService.deleteSubSection(selectedXpertKnowledge!.id, selectedXpertKnowledgeTutorial!.id, sectionId, subSectionId)
      setXpertKnowledgeSections((prev) =>
        prev.map((section) => ({
          ...section,
          subSections: section.subSections?.filter((subSection) => subSection.id !== subSectionId),
        }))
      )
    } catch (error) {
      console.error('Error deleting Xpert Knowledge Tutorial SubSection:', error)
    }
  }

  const createXpertKnowledgeTutorialSection = async (title: string, position: number, intro?: string, conclusion?: string) => {
    try {
      const section = await XpertKnowledgeSectionService.createSection(selectedXpertKnowledge!.id, selectedXpertKnowledgeTutorial!.id, {
        title,
        position,
        intro,
        conclusion,
      })
      setXpertKnowledgeSections((prev) => [...prev, section])
      return section
    } catch (error) {
      console.error('Error creating Xpert Knowledge Tutorial Section:', error)
    }
  }

  const createXpertKnowledgeTutorialSubSection = async (sectionId: number, title: string, position: number, content?: string) => {
    try {
      const subSection = await XpertKnowledgeSectionService.createSubSection(
        selectedXpertKnowledge!.id,
        selectedXpertKnowledgeTutorial!.id,
        sectionId,
        {title, position, content}
      )
      setXpertKnowledgeSections((prev) =>
        prev.map((section) => ({
          ...section,
          subSections: section.subSections ? [...section.subSections, subSection] : [subSection],
        }))
      )
    } catch (error) {
      console.error('Error creating Xpert Knowledge Tutorial SubSection:', error)
    }
  }

  const updateXpertKnowledgeTutorialSection = async (sectionId: number, title: string, intro?: string, conclusion?: string) => {
    try {
      const updatedSection = await XpertKnowledgeSectionService.updateSection(
        selectedXpertKnowledge!.id,
        selectedXpertKnowledgeTutorial!.id,
        sectionId,
        {title, intro, conclusion}
      )
      setXpertKnowledgeSections((prev) => prev.map((section) => (section.id === sectionId ? updatedSection : section)))
    } catch (error) {
      console.error('Error updating Xpert Knowledge Tutorial Section:', error)
    }
  }

  const updateXpertKnowledgeTutorialSubSection = async (
    sectionId: number,
    subSectionId: number,
    title: string,
    medias: XpertKnowledgeSubSectionBlobDto[],
    content?: string,
    position?: number
  ) => {
    try {
      const updatedSubSection = await XpertKnowledgeSectionService.updateSubSection(
        selectedXpertKnowledge!.id,
        selectedXpertKnowledgeTutorial!.id,
        sectionId,
        subSectionId,
        {title, content, position, medias}
      )
      setXpertKnowledgeSections((prev) =>
        prev.map((section) => ({
          ...section,
          subSections: section.subSections?.map((subSection) => (subSection.id === subSectionId ? updatedSubSection : subSection)),
        }))
      )
    } catch (error) {
      console.error('Error updating Xpert Knowledge Tutorial SubSection:', error)
    }
  }

  return (
    <Context.Provider
      value={{
        xpertKnowledgeSections,
        xpertKnowledgeTutorials,
        selectedXpertKnowledgeTutorial,
        downgradeXpertKnowledgeTutorial,
        submitRating,
        setSelectedXpertKnowledgeTutorial,
        updateXpertKnowledgeTutorial,
        submitXpertKnowledgeTutorial,
        requestChangeXpertKnowledgeTutorial,
        dismissRequestChangeXpertKnowledgeTutorial,
        dismissReviewXpertKnowledgeTutorial,
        approveXpertKnowledgeTutorial,
        deleteXpertKnowledgeTutorialSection,
        deleteXpertKnowledgeTutorialSubSection,
        createXpertKnowledgeTutorialSection,
        createXpertKnowledgeTutorialSubSection,
        updateXpertKnowledgeTutorialSection,
        updateXpertKnowledgeTutorialSubSection,
        updateXpertKnowledgeSubclip,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useXpertKnowledgeTutorialContext = () => useContext(Context)
